import React from "react";

import { Pause, PlayArrow } from "@mui/icons-material";
import { Box, CircularProgress, IconButton, useTheme } from "@mui/material";

import useTruncateString from "hooks/useTruncateString";

import PropTypes from "prop-types";

function Song(props) {
  const {
    idx,
    currentIndex,
    setCurrentIndex,
    songName,
    audioStatus,
    setAudioStatus,
    noAudio,
  } = props;

  const mainTheme = useTheme();

  const handleTruncatedString = useTruncateString();

  let audioStatusIcon;

  switch (true) {
    case !audioStatus.isReady && audioStatus.isPlaying && currentIndex === idx:
      audioStatusIcon = (
        <CircularProgress
          color="whitePrimary"
          size={24}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            marginTop: "-12px",
            marginLeft: "-12px",
          }}
        />
      );
      break;
    case currentIndex === idx && audioStatus.isPlaying:
      audioStatusIcon = (
        <Pause sx={{ color: mainTheme.palette.whitePrimary.main }} />
      );
      break;
    default:
      audioStatusIcon = (
        <PlayArrow sx={{ color: mainTheme.palette.whitePrimary.main }} />
      );
  }

  const togglePlay = () => {
    if (currentIndex === idx) {
      // If the clicked song is already playing, pause it
      setAudioStatus((prev) => ({
        ...prev,
        isPlaying: !prev.isPlaying,
      }));
    } else {
      // If a different song is clicked, pause the currently playing song (if any)
      if (audioStatus.isPlaying) {
        setAudioStatus({
          ...audioStatus,
          isPlaying: false,
        });
      }

      // Play the clicked song and update the currentIndex
      setCurrentIndex(idx);
      setAudioStatus({
        isPlaying: true,
      });
    }
  };

  return (
    <Box>
      <Box
        component="article"
        sx={{
          paddingTop: "0.5rem",
          paddingBottom: "1rem",
          display: "block",
          paddingRight: "1.5rem",
          paddingLeft: "1.5rem",
          [mainTheme.breakpoints.down(700)]: {
            paddingRight: 0,
            paddingLeft: 0,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            padding: "0.5rem 1rem",
            textAlign: "left",
          }}
        >
          {handleTruncatedString({
            customText: songName,
            customVariant: "h5",
            customComponent: "p",
            customSx: {
              paddingRight: "1rem",
            },
            maxCharacters: {
              sm: 13,
              smToMd: 38,
              aboveMd: 72,
            },
          })}
          <Box
            sx={{
              display: "flex",
            }}
          >
            <IconButton
              disabled={noAudio}
              onClick={() => {
                if (!noAudio) {
                  togglePlay();
                }
              }}
              sx={{
                borderRadius: "50%",
                width: "2.5rem",
                height: "2.5rem",
                backgroundColor: mainTheme.palette.darkPinkPrimary.main,
                "&:disabled": {
                  bgcolor: mainTheme.palette.lightGraySecondary.main,
                },
                "&:hover": {
                  bgcolor: mainTheme.palette.darkPinkPrimary.dark,
                },
              }}
            >
              {audioStatusIcon}
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

Song.propTypes = {
  idx: PropTypes.number.isRequired,
  currentIndex: PropTypes.number.isRequired,
  setCurrentIndex: PropTypes.func.isRequired,
  songName: PropTypes.string.isRequired,
  audioStatus: PropTypes.shape({
    isReady: PropTypes.bool.isRequired,
    isPlaying: PropTypes.bool.isRequired,
  }).isRequired,
  setAudioStatus: PropTypes.func.isRequired,
  noAudio: PropTypes.bool.isRequired,
};

export default Song;
